<script lang="ts">
    import {computePosition, flip, shift} from '@floating-ui/dom';
    import SvgIcon from '$lib/components/assets/SvgIcon.svelte';
    import type {UserWordsMapStore} from '$lib/classes/stores/get-user-words-map';
    import type {Tooltip} from '$lib/classes/_types/tooltip';
    import {Status} from '$lib/classes/data/user-word';
    import type {UserActionProcessor} from '$lib/classes/services/user-action-processor';
    import {getContext} from 'svelte';
    import {keyUserActionProcessor} from '$lib/context-keys';
    import type {WordId} from '$lib/classes/data/_primitives';

    export let userWordsMap: UserWordsMapStore
    export let tooltip: Tooltip
    export let onNavigate: (wordId: WordId) => void

    const userActionProcessor: UserActionProcessor = getContext(keyUserActionProcessor)

    $: isWordId = $tooltip.id.includes('/')
    $: wordStatus = $userWordsMap[$tooltip.id]?.status.toString()

    let top = 0
    let left = 0

    async function updateTopLeft(tooltipElem: HTMLElement, anchor: HTMLElement) {
        const {x, y} = await computePosition(anchor, tooltipElem, {
            placement: 'top',
            middleware: [flip(), shift({padding: 6})],
        })
        top = y
        left = x
    }

    function position(tooltipElem: HTMLElement, anchor: HTMLElement) {
        updateTopLeft(tooltipElem, anchor)
        return {
            update(anchor: HTMLElement) {
                updateTopLeft(tooltipElem, anchor)
            }
        }
    }

    function showTooltip() {
        $tooltip.hovered = true
    }

    function hideTooltip() {
        $tooltip.hovered = false
    }

    function handleClick(btnStatus: Status) {
        userActionProcessor.saveWord($tooltip.id, btnStatus)
    }

    function navigate() {
        onNavigate($tooltip.id)
    }
</script>

<!-- svelte-ignore a11y-click-events-have-key-events  a11y-no-static-element-interactions -->
<div class="tooltip-word-link"
     use:position={$tooltip.anchor}
     style:top={top + 'px'}
     style:left={left + 'px'}
     on:mouseenter={showTooltip}
     on:mouseleave={hideTooltip}
>
    {#if isWordId}
        <div class="tooltip-word-link-word-id">
            <div class="btns">
                <button on:click|stopPropagation={() => handleClick(Status.Known)}
                        class:saved={wordStatus === 'KNOWN'}
                        data-testid="tooltip-known-btn"
                >
                    <SvgIcon name="known" width="25px" height="25px" viewBox="0 0 32 32"/>
                </button>
                <button on:click|stopPropagation={() => handleClick(Status.Learn)}
                        class:saved={wordStatus === 'LEARN'}
                        data-testid="tooltip-learn-btn"
                >
                    <SvgIcon name="learn" width="23px" height="23px" viewBox="0 -2 32 32"/>
                </button>
                <button class="tooltip-word-link-btn-navigate"
                        on:click|stopPropagation={navigate}
                        data-testid="tooltip-browse-btn"
                >
                    <SvgIcon name="browse" width="23px" height="23px" viewBox="-4 -2 36 36"/>
                </button>
            </div>
            <div class="tooltip tooltip-translation"
                 on:click|stopPropagation
            >
                {$tooltip.translation}
            </div>
        </div>
    {:else if ['name', 'city', 'state', 'abbr.'].includes($tooltip.id)}
        <div class="tooltip tooltip-special">
            {$tooltip.id}
        </div>
    {:else if ['collocation', '-'].includes($tooltip.id)}
        <div class="tooltip tooltip-special">
            {$tooltip.translation}
        </div>
    {/if}
    <!-- NOTE no tooltip for 'number' -->
</div>


<style>
    .tooltip-word-link {
        position: absolute;
        display: flex;
        flex-direction: column;
        padding-block: .2rem;
        z-index: var(--z-index-word-link-tooltip);
        --border-radius: 3px;
        --gap: 3px;
    }

    .tooltip-word-link-word-id {
        display: grid;
        gap: var(--gap);
        filter: drop-shadow(-1px 2px 4px rgba(0, 0, 0, .2));
    }

    .btns {
        display: flex;
        gap: var(--gap);
        justify-self: center;
    }

    button {
        display: grid;
        place-content: center;
        padding: 0;
        margin: 0;
        width: 2.4rem;
        height: 2.4rem;
    }

    .saved {
        background: var(--color-accent);
        color: white;
    }

    button:active,
    button:hover {
        box-shadow: inset 0 0 0 2px var(--color-accent);
    }

    .tooltip {
        white-space: nowrap;
        padding: 6px 8px;
        font-size: .95rem;
        text-align: center;
        user-select: none;
        background: var(--color-accent);
        color: white;
    }

    .tooltip-translation {
        font-weight: var(--font-emph);
        border-radius: var(--border-radius);
    }

    .tooltip-special {
        border-radius: var(--border-radius);
        font-style: italic;
    }
</style>